import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { Button, Modal, ModalBody } from "reactstrap";
import { FaChevronLeft, FaChevronRight, FaTimes } from "react-icons/fa";
import { resolveMediaUrl } from "utils/mediaUrl";
import { getActiveColorStyle } from "utils/colorUtils";
import { copyTextToClipboard } from "utils/clipboard";
import { useNotification } from "contexts/NotificationContext";
import { useUpdateInventoryWebsite } from "api/inventory-website/service";

const MAX_IMAGES = 20;
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_DIMENSION = 4096;
const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];

const normalizeImages = (images) => {
  if (!Array.isArray(images)) return [];
  return images
    .map((item) => {
      if (typeof item === "string") return item;
      if (item && typeof item === "object") {
        return item.url || item.src || item.thumbnail_url || item.file_url || null;
      }
      return null;
    })
    .filter(Boolean);
};

const validateImageDimensions = (file) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve({
        isValid: img.width <= MAX_DIMENSION && img.height <= MAX_DIMENSION,
        width: img.width,
        height: img.height,
      });
    };
    img.onerror = () => resolve({ isValid: false, width: 0, height: 0 });
    img.src = URL.createObjectURL(file);
  });

const WebsiteImagesTab = ({
  websiteId,
  images,
  isLocked = false,
  activeColor = "blue",
  onImagesChange,
}) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const updateMutation = useUpdateInventoryWebsite();
  const fileInputRef = useRef(null);
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);

  const [pendingFiles, setPendingFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const darkMode = localStorage.getItem("darkMode");
    return darkMode ? JSON.parse(darkMode) : true;
  });

  const imageList = useMemo(() => normalizeImages(images), [images]);
  const resolvedImages = useMemo(
    () => imageList.map((url) => resolveMediaUrl(url) || url),
    [imageList]
  );
  const remainingSlots = Math.max(0, MAX_IMAGES - imageList.length - pendingFiles.length);
  const atLimit = remainingSlots <= 0;
  const lightboxOpen = lightboxIndex != null;
  const validPending = pendingFiles.filter(
    (file) =>
      file.validation?.typeValid &&
      file.validation?.sizeValid &&
      file.validation?.dimensionValid
  );

  useEffect(() => {
    const checkTheme = () => {
      const darkMode = localStorage.getItem("darkMode");
      setIsDarkMode(darkMode ? JSON.parse(darkMode) : true);
    };
    checkTheme();
    window.addEventListener("storage", checkTheme);
    const interval = setInterval(checkTheme, 500);
    return () => {
      window.removeEventListener("storage", checkTheme);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setLightboxIndex(null);
      if (e.key === "ArrowLeft") {
        setLightboxIndex((prev) =>
          prev == null ? prev : prev === 0 ? resolvedImages.length - 1 : prev - 1
        );
      }
      if (e.key === "ArrowRight") {
        setLightboxIndex((prev) =>
          prev == null ? prev : prev === resolvedImages.length - 1 ? 0 : prev + 1
        );
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [lightboxOpen, resolvedImages.length]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return `0 ${t("imagesTab.bytes")}`;
    const k = 1024;
    const sizes = [
      t("imagesTab.bytes"),
      t("imagesTab.kb"),
      t("imagesTab.mb"),
      t("imagesTab.gb"),
    ];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const handleFiles = async (fileList) => {
    if (isLocked || atLimit) return;
    const files = Array.from(fileList || []);
    const slotsAvailable = MAX_IMAGES - imageList.length - pendingFiles.length;
    if (slotsAvailable <= 0) {
      showNotification("warning", t("imagesTab.maxImagesReached", { max: MAX_IMAGES }));
      return;
    }

    const filesToProcess = files.slice(0, slotsAvailable);
    if (files.length > slotsAvailable) {
      showNotification(
        "warning",
        t("imagesTab.tooManyImagesSelected", { count: slotsAvailable })
      );
    }

    const processedFiles = [];
    for (const file of filesToProcess) {
      const typeValid = ALLOWED_TYPES.includes(file.type);
      const sizeValid = file.size <= MAX_FILE_SIZE;
      const dimensionCheck = await validateImageDimensions(file);
      file.validation = {
        typeValid,
        sizeValid,
        dimensionValid: dimensionCheck.isValid,
        width: dimensionCheck.width,
        height: dimensionCheck.height,
      };
      file.dimensions = {
        width: dimensionCheck.width,
        height: dimensionCheck.height,
      };
      file.previewUrl = URL.createObjectURL(file);
      processedFiles.push(file);
    }

    setPendingFiles((prev) => [...prev, ...processedFiles]);
  };

  const removePendingFile = (index) => {
    setPendingFiles((prev) => {
      const next = [...prev];
      const [removed] = next.splice(index, 1);
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return next;
    });
  };

  const handleUpload = async () => {
    if (!websiteId || validPending.length === 0 || isLocked) return;
    setIsUploading(true);
    try {
      const result = await updateMutation.mutateAsync({
        id: websiteId,
        data: {},
        files: validPending,
      });
      pendingFiles.forEach((file) => {
        if (file.previewUrl) URL.revokeObjectURL(file.previewUrl);
      });
      setPendingFiles([]);
      if (typeof onImagesChange === "function") {
        onImagesChange(normalizeImages(result?.images));
      }
      showNotification("success", t("inventoryWebsite.imagesUploadSuccess"));
    } catch (error) {
      showNotification("error", t("inventoryWebsite.imagesUploadError"));
    } finally {
      setIsUploading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (deleteIndex == null || isLocked) return;
    const next = imageList.filter((_, index) => index !== deleteIndex);
    try {
      const result = await updateMutation.mutateAsync({
        id: websiteId,
        data: { images: next },
      });
      if (typeof onImagesChange === "function") {
        onImagesChange(normalizeImages(result?.images ?? next));
      }
      showNotification("success", t("inventoryWebsite.imageDeleteSuccess"));
    } catch (error) {
      showNotification("error", t("inventoryWebsite.imageDeleteError"));
    } finally {
      setDeleteIndex(null);
    }
  };

  const dropBorder = isDragOver
    ? isDarkMode
      ? "2px dashed #e14eca"
      : `2px dashed ${activeColorStyle.borderColor}`
    : isDarkMode
      ? "2px dashed #344675"
      : "2px dashed #6c757d";

  return (
    <div className="row website-images-tab">
      <style>{`
        .website-images-tab .card,
        .website-images-tab .card-img-top,
        .website-images-tab .card-header,
        .website-images-tab .card-body,
        .website-images-tab .card-footer,
        .website-images-tab .btn,
        .website-images-tab img {
          border-radius: 0 !important;
        }
      `}</style>
      {!isLocked && (
        <div className="col-md-4 mb-4">
          <div className="card">
            <div className="card-header font-weight-bold d-flex justify-content-between align-items-center">
              <span>{t("imagesTab.uploadImages")}</span>
              <small className="text-muted">
                {t("imagesTab.imagesCount", {
                  current: imageList.length,
                  max: MAX_IMAGES,
                })}
              </small>
            </div>
            <div className="card-body">
              <div
                onDragOver={(e) => {
                  if (atLimit) return;
                  e.preventDefault();
                  setIsDragOver(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  setIsDragOver(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragOver(false);
                  handleFiles(e.dataTransfer.files);
                }}
                onClick={() => {
                  if (!atLimit) fileInputRef.current?.click();
                }}
                style={{
                  border: dropBorder,
                  padding: "40px 20px",
                  textAlign: "center",
                  cursor: atLimit ? "not-allowed" : "pointer",
                  opacity: atLimit ? 0.6 : 1,
                  backgroundColor: isDragOver
                    ? isDarkMode
                      ? "#1e1e2d"
                      : "#e3f2fd"
                    : isDarkMode
                      ? "#27293d"
                      : "#f8f9fa",
                  marginBottom: 20,
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <div style={{ position: "relative", zIndex: 1 }}>
                  <i
                    className="tim-icons icon-upload"
                    style={{
                      fontSize: 36,
                      color: isDarkMode ? "#6c757d" : "#adb5bd",
                      marginBottom: 10,
                      display: "block",
                    }}
                  />
                  <h6 className="mb-2" style={{ fontWeight: 600 }}>
                    {atLimit
                      ? t("imagesTab.maxImagesReached", { max: MAX_IMAGES })
                      : t("imagesTab.uploadImages")}
                  </h6>
                  <p className="text-muted mb-0">
                    {atLimit
                      ? t("imagesTab.removeImagesToUploadMore")
                      : t("imagesTab.dragAndDropImages")}
                  </p>
                  {!atLimit && remainingSlots < MAX_IMAGES && (
                    <div style={{ marginTop: 10, fontSize: 12 }} className="text-muted">
                      {t("imagesTab.remainingImageSlots", { count: remainingSlots })}
                    </div>
                  )}
                  <div style={{ marginTop: 15, fontSize: 12 }} className="text-muted">
                    {t("imagesTab.supportsFormats")}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 12 }} className="text-muted">
                    {t("imagesTab.maxImagesPerVehicle", { max: MAX_IMAGES })}
                  </div>
                  <div style={{ marginTop: 8, fontSize: 11 }} className="text-muted">
                    {t("imagesTab.recommendedDimensions")}
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  disabled={atLimit}
                  onChange={(e) => {
                    handleFiles(e.target.files);
                    e.target.value = "";
                  }}
                  style={{ display: "none" }}
                />
              </div>

              {pendingFiles.length > 0 && (
                <div>
                  <h6 className="mb-3" style={{ fontWeight: 600 }}>
                    {t("inventoryWebsite.selectedFiles", {
                      count: pendingFiles.length,
                      defaultValue: `Selected Files (${pendingFiles.length})`,
                    })}
                  </h6>
                  <div className="row">
                    {pendingFiles.map((file, index) => {
                      const isValid =
                        file.validation?.typeValid &&
                        file.validation?.sizeValid &&
                        file.validation?.dimensionValid;
                      return (
                        <div className="col-6 mb-2" key={`${file.name}-${index}`}>
                          <div
                            className="card"
                            style={{
                              border: isValid ? "1px solid rgba(0,0,0,0.1)" : "2px solid #fd5d93",
                              overflow: "hidden",
                            }}
                          >
                            <img
                              src={file.previewUrl}
                              alt={`Preview ${index + 1}`}
                              className="card-img-top"
                              style={{ height: 80, objectFit: "cover" }}
                            />
                            <div className="card-body p-2">
                              <small className="d-block text-truncate">{file.name}</small>
                              <small className="text-muted d-block" style={{ fontSize: 10 }}>
                                {formatFileSize(file.size)} •{" "}
                                {(file.type.split("/")[1] || "").toUpperCase()}
                              </small>
                              {file.dimensions && (
                                <small className="text-muted d-block" style={{ fontSize: 10 }}>
                                  {file.dimensions.width} × {file.dimensions.height}px
                                </small>
                              )}
                              {!isValid && (
                                <div style={{ marginTop: 5 }}>
                                  {!file.validation?.typeValid && (
                                    <small className="text-danger d-block" style={{ fontSize: 10 }}>
                                      {t("imagesTab.invalidFileType")}
                                    </small>
                                  )}
                                  {!file.validation?.sizeValid && (
                                    <small className="text-danger d-block" style={{ fontSize: 10 }}>
                                      {t("imagesTab.fileTooLarge")}
                                    </small>
                                  )}
                                  {!file.validation?.dimensionValid && (
                                    <small className="text-danger d-block" style={{ fontSize: 10 }}>
                                      {t("imagesTab.invalidDimensions")}
                                    </small>
                                  )}
                                </div>
                              )}
                              <button
                                type="button"
                                className="btn btn-sm btn-danger mt-1"
                                onClick={() => removePendingFile(index)}
                                style={{ fontSize: 12 }}
                              >
                                <i className="tim-icons icon-simple-remove" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <button
                    type="button"
                    className="btn btn-sm btn-block mt-3"
                    onClick={handleUpload}
                    disabled={isUploading || validPending.length === 0}
                    style={{
                      ...(validPending.length > 0
                        ? activeColorStyle
                        : {
                            background: "#6c757d",
                            borderColor: "#6c757d",
                          }),
                      color: "white",
                      fontWeight: 600,
                      opacity: validPending.length > 0 ? 1 : 0.6,
                    }}
                  >
                    {isUploading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        />
                        {t("inventoryWebsite.uploading")}
                      </>
                    ) : validPending.length > 0 ? (
                      t("imagesTab.uploadImagesButton")
                    ) : (
                      t("imagesTab.noValidFilesToUpload")
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className={`${isLocked ? "col-md-12" : "col-md-8"} mb-4`}>
        <div className="card">
          <div className="card-header font-weight-bold">
            {t("imagesTab.imageGallery")}
          </div>
          <div className="card-body">
            {imageList.length > 0 ? (
              <div className="row">
                {imageList.map((url, index) => (
                  <div className="col-md-3 mb-3" key={`${url}-${index}`}>
                    <div
                      className="card"
                      style={{ cursor: "pointer", overflow: "hidden" }}
                      onClick={() => setLightboxIndex(index)}
                    >
                      <div style={{ position: "relative" }}>
                        <img
                          src={resolvedImages[index]}
                          alt={`Vehicle ${index + 1}`}
                          className="card-img-top"
                          style={{ height: 150, objectFit: "cover" }}
                        />
                        <div
                          style={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            background: "rgba(0,0,0,0.7)",
                            width: 32,
                            height: 32,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            fontSize: 14,
                          }}
                        >
                          <i className="tim-icons icon-image-02" />
                        </div>
                      </div>
                      <div className="card-body p-2">
                        <div className="d-flex justify-content-between align-items-center">
                          <small className="text-muted" style={{ fontSize: 11 }}>
                            Click to view
                          </small>
                          <div className="d-flex" style={{ gap: 4 }}>
                            {!isLocked && (
                              <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteIndex(index);
                                }}
                                style={{ fontSize: 12 }}
                              >
                                <i className="tim-icons icon-trash-simple" />
                              </button>
                            )}
                            <button
                              type="button"
                              className="btn btn-sm"
                              onClick={async (e) => {
                                e.stopPropagation();
                                const copied = await copyTextToClipboard(resolvedImages[index]);
                                if (copied) {
                                  showNotification(
                                    "success",
                                    t("filesTab.linkCopied", "Link copied!")
                                  );
                                } else {
                                  showNotification(
                                    "warning",
                                    t("filesTab.linkCopyFailed", "Could not copy link.")
                                  );
                                }
                              }}
                              style={{
                                fontSize: 12,
                                width: 32,
                                height: 32,
                                padding: 0,
                                backgroundColor: "#344767",
                                borderColor: "#344767",
                                color: "#fff",
                              }}
                              title={t("filesTab.copyLink", "Copy file link")}
                            >
                              <i className="fas fa-link" style={{ color: "#fff" }} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <h5 className="card-title">{t("imagesTab.noImagesFound")}</h5>
                <p className="text-muted">{t("imagesTab.pleaseUploadImages")}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={deleteIndex != null}
        toggle={() => setDeleteIndex(null)}
        centered={false}
        style={{ marginTop: 100 }}
      >
        <div className="modal-header justify-content-center">
          <button
            type="button"
            className="close"
            onClick={() => setDeleteIndex(null)}
          >
            <i className="tim-icons icon-simple-remove" />
          </button>
          <h6 className="title title-up">{t("imagesTab.deleteImage")}</h6>
        </div>
        <ModalBody className="text-center">
          <p>{t("inventoryWebsite.imageDeleteConfirm")}</p>
          <p className="text-muted">{t("imagesTab.thisActionCannotBeUndone")}</p>
        </ModalBody>
        <div className="modal-footer">
          <Button color="secondary" onClick={() => setDeleteIndex(null)}>
            {t("inventoryWebsite.cancel")}
          </Button>
          <Button color="danger" onClick={handleConfirmDelete}>
            {t("inventoryWebsite.remove")}
          </Button>
        </div>
      </Modal>

      {lightboxOpen &&
        ReactDOM.createPortal(
          <div
            onClick={() => setLightboxIndex(null)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.85)",
              zIndex: 10050,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 24,
            }}
          >
            <button
              type="button"
              onClick={() => setLightboxIndex(null)}
              style={{
                position: "absolute",
                top: 16,
                right: 16,
                background: "transparent",
                border: "none",
                color: "#fff",
                fontSize: 22,
                cursor: "pointer",
              }}
            >
              <FaTimes />
            </button>
            {resolvedImages.length > 1 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex((prev) =>
                    prev === 0 ? resolvedImages.length - 1 : prev - 1
                  );
                }}
                style={{
                  position: "absolute",
                  left: 16,
                  background: "rgba(255,255,255,0.15)",
                  border: "none",
                  color: "#fff",
                  width: 44,
                  height: 44,
                  cursor: "pointer",
                }}
              >
                <FaChevronLeft />
              </button>
            )}
            <img
              src={resolvedImages[lightboxIndex]}
              alt={`Vehicle ${lightboxIndex + 1}`}
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: "90vw",
                maxHeight: "85vh",
                objectFit: "contain",
                background: "#111",
              }}
            />
            {resolvedImages.length > 1 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex((prev) =>
                    prev === resolvedImages.length - 1 ? 0 : prev + 1
                  );
                }}
                style={{
                  position: "absolute",
                  right: 16,
                  background: "rgba(255,255,255,0.15)",
                  border: "none",
                  color: "#fff",
                  width: 44,
                  height: 44,
                  cursor: "pointer",
                }}
              >
                <FaChevronRight />
              </button>
            )}
            <div
              style={{
                position: "absolute",
                bottom: 16,
                color: "#fff",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {lightboxIndex + 1} / {resolvedImages.length}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default WebsiteImagesTab;
